import { joiResolver } from "@hookform/resolvers/joi";
import { Add as AddIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm as useFormController, useWatch } from "react-hook-form";

import {
    EventFormat,
    MeasuringType,
    Unit,
    checkIsMovingDisciplineCategory,
    formatMeasuringType,
    formatResultValue,
    generateFullName,
    getDiscipline,
    getDisciplineCategoryOfDiscipline,
    parseResultValue,
    resultValidationSchemas,
    unitToResultValueMaskMap,
    validationOptions,
} from "@bujus/common";
import { useApiServiceContext } from "@bujus/common-frontend";

import { Collapse, DialogClosingButton, Form, RadioSelect } from "@/base";
import { useSelectedEventStateContext } from "@/contexts";
import { useSnackbarsController } from "@/hooks";

import {
    DisciplineSelect,
    HelperSelect,
    ParticipantSelect,
    ResultAttempts,
    ResultValueInput,
} from "../../common";

const ResultCreationDialog = ({ close, isOpen }) => {
    const snackbarsController = useSnackbarsController();

    const queryClient = useQueryClient();

    const apiService = useApiServiceContext();
    const [selectedEvent] = useSelectedEventStateContext();

    const formController = useFormController({
        mode: "onChange",
        resolver: joiResolver(resultValidationSchemas.creation.forms.school, validationOptions),
        context: { event: selectedEvent },
        defaultValues: {
            helper: null,
            participant: null,
            discipline: null,
            ...(selectedEvent.format === EventFormat.COMPETITION && {
                measuringType: MeasuringType.MANUALLY,
            }),
            isFailedAttempt: false,
            value: unitToResultValueMaskMap[Unit.SECONDS].defaultValue,
        },
    });
    const participantFieldValue = useWatch({
        control: formController.control,
        name: "participant",
    });
    const disciplineFieldValue = useWatch({
        control: formController.control,
        name: "discipline",
    });

    const mutation = useMutation(
        async (variables) => apiService.results.createAsync(variables.result),
        {
            onError: (error) => {
                snackbarsController.enqueueError(error);
            },
            onSuccess: (result) => {
                snackbarsController.enqueueSuccessMessage(
                    `${generateFullName(
                        result.participant.name.first,
                        result.participant.name.last,
                    )}: ${getDiscipline(selectedEvent.format, result.disciplineId).name}: ${formatResultValue(
                        selectedEvent.format,
                        result.disciplineId,
                        result.isFailedAttempt,
                        result.value,
                    )} hinzugefügt`,
                );
                queryClient.invalidateQueries(["results"]);
                close();
            },
        },
    );

    const selectedDisciplineCategory =
        disciplineFieldValue === null
            ? undefined
            : getDisciplineCategoryOfDiscipline(selectedEvent.format, disciplineFieldValue.id);

    const tryClose = () => {
        if (mutation.isLoading) {
            return false;
        }
        close();
        return true;
    };

    const handleClose = () => {
        tryClose();
    };

    const handleFormSubmit = (data) => {
        if (mutation.isLoading) {
            return;
        }
        mutation.mutate({
            result: {
                ...(data.helper !== null && { helperId: data.helper.id }),
                participantId: data.participant.id,
                disciplineId: data.discipline.id,
                ...(selectedEvent.format === EventFormat.COMPETITION &&
                    checkIsMovingDisciplineCategory(
                        getDisciplineCategoryOfDiscipline(selectedEvent.format, data.discipline.id)
                            .id,
                    ) && { measuringType: data.measuringType }),
                isFailedAttempt: data.isFailedAttempt,
                ...(!data.isFailedAttempt && {
                    value: parseResultValue(selectedEvent.format, data.discipline.id, data.value),
                }),
            },
        });
    };

    const handleClosingButtonClick = () => {
        tryClose();
    };

    return (
        <Dialog onClose={handleClose} open={isOpen}>
            <Form controller={formController} onSubmit={handleFormSubmit}>
                <DialogTitle>Ergebnis eintragen</DialogTitle>
                <DialogClosingButton
                    isDisabled={mutation.isLoading}
                    onClick={handleClosingButtonClick}
                />
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <HelperSelect
                            fieldName="helper"
                            isEnabled={!mutation.isLoading}
                            label="Helfer"
                        />
                        <ParticipantSelect
                            fieldName="participant"
                            isEnabled={!mutation.isLoading}
                            label="Teilnehmer *"
                        />
                        <DisciplineSelect
                            fieldName="discipline"
                            isEnabled={participantFieldValue !== null && !mutation.isLoading}
                            label="Disziplin *"
                            participant={
                                participantFieldValue === null ? undefined : participantFieldValue
                            }
                        />
                        {selectedEvent.format === EventFormat.COMPETITION && (
                            <Collapse
                                isOpen={
                                    selectedDisciplineCategory !== undefined &&
                                    checkIsMovingDisciplineCategory(selectedDisciplineCategory.id)
                                }
                                overwritingTopMargin={2}
                            >
                                <RadioSelect
                                    fieldName="measuringType"
                                    formatItem={formatMeasuringType}
                                    isEnabled={
                                        disciplineFieldValue !== undefined && !mutation.isLoading
                                    }
                                    items={Object.values(MeasuringType)}
                                    label="Messtyp"
                                />
                            </Collapse>
                        )}
                        <ResultValueInput
                            discipline={
                                disciplineFieldValue === null ? undefined : disciplineFieldValue
                            }
                            fieldName="value"
                            isEnabled={disciplineFieldValue !== undefined && !mutation.isLoading}
                            isFailedAttemptFieldName="isFailedAttempt"
                            label="Wert *"
                        />
                        <ResultAttempts
                            discipline={
                                disciplineFieldValue === null ? undefined : disciplineFieldValue
                            }
                            participant={
                                participantFieldValue === null ? undefined : participantFieldValue
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        loading={mutation.isLoading}
                        startIcon={<AddIcon />}
                        type="submit"
                    >
                        Eintragen
                    </LoadingButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
};

export { ResultCreationDialog };
